import React, { useEffect, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { hasAdminAccess } from '../utils/authUser';
import './AgentInfo.css';

const isAdminUser = () => {
  try { return hasAdminAccess(JSON.parse(localStorage.getItem('user'))); } catch { return false; }
};

const SocialIcon = ({ network }) => {
  const iconProps = { viewBox: '0 0 24 24', 'aria-hidden': true, focusable: 'false' };

  switch (network) {
    case 'Facebook':
      return <svg {...iconProps}><path d="M14.5 8H17V4.3c-.4-.1-1.8-.3-3.4-.3C10.4 4 8 6 8 9.7V13H4v4h4v7h4.5v-7h3.7l.6-4h-4.3V10c0-1.2.3-2 2-2Z" /></svg>;
    case 'Instagram':
      return <svg {...iconProps} className="social-icon-outline"><rect x="3.5" y="3.5" width="17" height="17" rx="5" /><circle cx="12" cy="12" r="4" /><circle className="social-icon-fill" cx="17.4" cy="6.8" r="1" /></svg>;
    case 'LinkedIn':
      return <svg {...iconProps}><path d="M5.2 8.4H9V20H5.2V8.4Zm1.9-5.7a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4ZM11.3 8.4H15v1.7h.1c.5-1 1.8-2.1 3.8-2.1 4 0 4.8 2.6 4.8 6.1V20h-3.9v-5.2c0-1.3 0-2.9-1.8-2.9s-2.1 1.4-2.1 2.8V20h-3.8V8.4h-.8Z" /></svg>;
    case 'YouTube':
      return <svg {...iconProps}><path d="M22.5 7.1a2.8 2.8 0 0 0-2-2C18.8 4.6 12 4.6 12 4.6s-6.8 0-8.5.5a2.8 2.8 0 0 0-2 2A29 29 0 0 0 1 12a29 29 0 0 0 .5 4.9 2.8 2.8 0 0 0 2 2c1.7.5 8.5.5 8.5.5s6.8 0 8.5-.5a2.8 2.8 0 0 0 2-2A29 29 0 0 0 23 12a29 29 0 0 0-.5-4.9ZM9.8 15.3V8.7l5.8 3.3-5.8 3.3Z" /></svg>;
    case 'TikTok':
      return <svg {...iconProps}><path d="M15.5 3c.3 2.1 1.5 3.4 3.5 3.8v3.1a8.3 8.3 0 0 1-3.5-1v6.3A5.8 5.8 0 1 1 10.4 9v3.2a2.7 2.7 0 1 0 1.9 2.6V3h3.2Z" /></svg>;
    default:
      return <svg {...iconProps}><path d="M4.3 4h4.6l4 5.4L17.5 4h2.2l-5.8 6.9L20.5 20h-4.6l-4.4-5.9L6.5 20H4.3l6.2-7.4L4.3 4Zm3.5 1.8 9 12.4h2L9.7 5.8h-2Z" /></svg>;
  }
};

export default function AgentInfo() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [bio, setBio] = useState('');
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const isAdmin = isAdminUser();

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    fetch(`https://tophamtechbe.com/llm/v1/agent-knowledge/${SITE_CONFIG.SITE_ID}/about`)
      .then((response) => response.ok ? response.json() : null)
      .then((data) => { if (data?.about) setBio(data.about); })
      .catch(() => {});
  }, []);

  const saveBio = async () => {
    const response = await fetch(`https://tophamtechbe.com/llm/v1/agent-knowledge/${SITE_CONFIG.SITE_ID}/about`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('jwt_token')}` },
      body: JSON.stringify({ about: editText, ...(SITE_CONFIG.AMPLIFY_WEBHOOK ? { rebuild_webhook: SITE_CONFIG.AMPLIFY_WEBHOOK } : {}) })
    });
    if (response.ok) { setBio(editText); setEditing(false); }
  };

  const title = `About Jeff Upp | Upland & Inland Empire REALTOR®`;
  const description = 'Meet Jeff Upp of RE/MAX Champions, a Southern California REALTOR® serving buyers and sellers since 1990. California DRE #01058008.';
  const socialLinks = [
    ['Facebook', SITE_CONFIG.AGENT.FACEBOOK],
    ['Instagram', SITE_CONFIG.AGENT.INSTAGRAM],
    ['LinkedIn', SITE_CONFIG.AGENT.LINKEDIN],
    ['YouTube', SITE_CONFIG.AGENT.YOUTUBE],
    ['TikTok', SITE_CONFIG.AGENT.TIKTOK],
    ['X', SITE_CONFIG.AGENT.TWITTER]
  ].filter(([, href]) => href);

  return (
    <main className={`agent-info-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <Helmet><title>{title}</title><meta name="description" content={description} /><link rel="canonical" href="https://jeffupp.com/about-us" /><meta property="og:title" content={title} /><meta property="og:description" content={description} /><meta property="og:url" content="https://jeffupp.com/about-us" /><meta property="og:image" content={SEO_CONFIG.OG_IMAGE} /></Helmet>
      <button className="agent-back-button" onClick={() => navigate('/')}>← Home</button>

      <section className="agent-hero">
        <div className="agent-hero-inner">
          <div className="agent-photo reveal in-view"><img src="/headshot.jpg" alt="Jeff Upp, REALTOR with RE/MAX Champions" /></div>
          <div className="agent-details reveal in-view">
            <span className="agent-eyebrow">Southern California Real Estate Since 1990</span>
            <h1>Jeff Upp</h1>
            <p className="agent-title">REALTOR® · RE/MAX Champions</p>
            <p className="agent-location">Upland · Inland Empire · Eastern Los Angeles County</p>
            <nav className="agent-social-links" aria-label="Jeff Upp on social media">
              {socialLinks.map(([network, href]) => (
                <a key={network} className="agent-social-link" href={href} target="_blank" rel="noreferrer" aria-label={`Visit Jeff Upp on ${network}`} title={network}>
                  <SocialIcon network={network} />
                </a>
              ))}
            </nav>
            <div className="contact-info">
              <a className="contact-item" href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}><span className="contact-icon">✉</span>{SITE_CONFIG.AGENT.EMAIL}</a>
              <a className="contact-item" href={`tel:${SITE_CONFIG.AGENT.PHONE}`}><span className="contact-icon">☎</span>(909) 519-1114</a>
              <div className="contact-item"><span className="contact-icon">⌂</span>California DRE #{SITE_CONFIG.AGENT.LICENSE}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="agent-stats reveal in-view">
        <div className="stat-item"><div className="stat-value">36</div><div className="stat-label">Years of Experience</div></div>
        <div className="stat-item"><div className="stat-value">1990</div><div className="stat-label">Serving Clients Since</div></div>
        <div className="stat-item"><div className="stat-value">4.9★</div><div className="stat-label">Realtor.com Rating</div></div>
        <div className="stat-item"><div className="stat-value">SFR</div><div className="stat-label">Specialist Designation</div></div>
      </section>

      <section className="agent-bio-section reveal in-view">
        <span className="agent-eyebrow">Experience with a personal touch</span>
        <h2>About Jeff {isAdmin && !editing && <button className="bio-edit-btn" onClick={() => { setEditText(bio); setEditing(true); }}>✎</button>}</h2>
        <div className="bio-content">
          {editing ? <div className="bio-edit-area"><textarea value={editText} onChange={(event) => setEditText(event.target.value)} rows={9} /><div className="bio-edit-actions"><button className="bio-save-btn" onClick={saveBio}>Save</button><button className="bio-cancel-btn" onClick={() => setEditing(false)}>Cancel</button></div></div> : bio && <p className="bio-lead">{bio}</p>}
          <div className="bio-block"><h3>Guidance Built on Experience</h3><p>Jeff has marketed and sold homes across Southern California since 1990. He works with first-time buyers, move-up and relocation clients, investors, and sellers whose transactions require careful coordination. More than three decades in the market give him a long view of pricing, negotiation, property condition, and the practical details that keep an escrow moving.</p></div>
          <div className="bio-block"><h3>From First Homes to Complex Sales</h3><p>His experience includes single-family homes, condos, townhomes, vacant land, investment and multi-unit properties, foreclosures, short sales, and trust or estate sales. Jeff holds the Short Sales and Foreclosure Resource designation and is known for explaining options clearly, staying available, and advocating steadily when decisions become complicated.</p></div>
          <div className="bio-block"><h3>Local, Personal Service</h3><p>Based at RE/MAX Champions in Upland, Jeff serves communities across the western Inland Empire and neighboring eastern Los Angeles County. His process starts with the client’s real priorities—timing, budget, commute, property type, and tolerance for repair—then turns those priorities into a focused plan supported by current local evidence.</p></div>
        </div>
      </section>

      <section className="agent-specialties reveal in-view">
        <div><span className="agent-eyebrow">Specialties</span><h2>How Jeff Helps</h2><ul><li>Buyer and seller representation</li><li>First-time homebuyers</li><li>Investment and multi-unit properties</li><li>Relocation</li><li>Trust and estate sales</li><li>Foreclosures and short sales</li><li>Condos, townhomes, land, and residential homes</li></ul></div>
        <div><span className="agent-eyebrow">Service Areas</span><h2>Local Markets</h2><p>{SITE_CONFIG.AGENT.SERVICE_AREAS.join(' · ')}</p></div>
      </section>

      <section className="agent-testimonials reveal in-view">
        <span className="agent-eyebrow">Verified client feedback</span><h2>What Clients Value</h2>
        <div className="agent-testimonial-grid">
          <blockquote><p>“Jeff’s knowledge of today’s market was outstanding. He communicated and taught us many different factors regarding the sale of our home.”</p><footer>Bruce Bugher · Seller, 2026</footer></blockquote>
          <blockquote><p>“He was there for me every step of the way—from finding the condo to handing me my keys.”</p><footer>Lorena Tarnay · First-time buyer, 2025</footer></blockquote>
          <blockquote><p>“Jeff is very professional, knowledgeable, and has a service attitude.”</p><footer>Dan &amp; June · Ontario, 2025</footer></blockquote>
        </div>
        <a className="reviews-link" href={SITE_CONFIG.AGENT.ZILLOW_PROFILE} target="_blank" rel="noreferrer">View Jeff’s Zillow profile</a>
      </section>

      <section className="agent-cta-section reveal in-view"><h2>Ready to Make a Move?</h2><p>Talk through your goals with an experienced local REALTOR®.</p><div className="agent-cta-buttons"><a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=Real Estate Inquiry`} className="cta-button primary">Email Jeff</a><a href={`tel:${SITE_CONFIG.AGENT.PHONE}`} className="cta-button secondary">Call (909) 519-1114</a><Link to="/search" className="cta-button secondary">Search Homes</Link></div></section>
    </main>
  );
}
